import type { WebhookConfig } from '@/types/custom-playbooks';
import type { RecordSkillOutput } from '@/types/record-skill';
import {
  outputFieldsError,
  outputSchema,
} from '@/containers/agents/record-skill-outputs.util';
import { BRAND } from '@/lib/brand';

const RESERVED_HEADERS = new Set([
  'host',
  'content-length',
  'content-type',
  'transfer-encoding',
  'connection',
  'cookie',
  'authorization',
  'user-agent',
  'idempotency-key',
]);
export function webhookHeaderAllowed(name: string): boolean {
  return (
    /^[A-Za-z0-9-]{1,64}$/.test(name) &&
    !RESERVED_HEADERS.has(name.toLowerCase()) &&
    !name.toLowerCase().startsWith('x-cleanlist-')
  );
}
export function webhookUrlValid(value: string): boolean {
  if (
    !value ||
    value.length > 2048 ||
    [...value].some((char) => char.charCodeAt(0) <= 32)
  )
    return false;
  try {
    const url = new URL(value);
    return (
      url.protocol === 'https:' &&
      (!url.port || url.port === '443') &&
      !!url.hostname &&
      !url.username &&
      !url.password &&
      !url.hostname.includes(':') &&
      !/^\d+\.\d+\.\d+\.\d+$/.test(url.hostname)
    );
  } catch {
    return false;
  }
}
export const webhookResponsePathValid = (path: string) =>
  path.length <= 200 &&
  /^\$(\.[A-Za-z_][A-Za-z0-9_]*|\[[0-9]{1,3}\])+$/.test(path);
const bounded = (value: number, maximum: number) =>
  Number.isInteger(value) && value >= 1 && value <= maximum;

/** Local completeness only; server validation owns DNS, saved secrets and fields. */
export function webhookConfigError(config: WebhookConfig): string | undefined {
  if (!webhookUrlValid(config.url))
    return 'Use an https address on port 443 with a host name';
  if (
    !['per_record', 'batch'].includes(config.mode ?? 'per_record') ||
    !bounded(config.batchSize ?? 100, 100)
  )
    return 'Pick per record or batches of up to 100';
  const headers = config.headers ?? [];
  if (
    headers.length > 10 ||
    headers.some(
      ({ name, value }) =>
        !webhookHeaderAllowed(name) ||
        value.length > 500 ||
        /[^\x20-\x7e]/.test(value)
    )
  )
    return `Use up to 10 static headers; delivery and authorization headers are set by ${BRAND.name}`;
  if (
    new Set(headers.map((item) => item.name.toLowerCase())).size !==
    headers.length
  )
    return 'Use each header name once';
  const auth = config.auth;
  if (auth && auth.type !== 'none') {
    if (
      !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
        auth.secretId
      )
    )
      return 'Pick a saved secret';
    if (auth.type === 'header' && !webhookHeaderAllowed(auth.headerName))
      return 'Pick a custom authentication header';
  }
  if (config.body != null) {
    try {
      for (const replacement of ['null', '"sample"'])
        JSON.parse(config.body.replace(/\{\{[\s\S]*?\}\}/g, replacement));
    } catch {
      return 'The body must be valid JSON. Insert fields as values without quotes.';
    }
  }
  if (config.outputs != null || config.responseMap != null) {
    const fields = config.outputs?.fields ?? [];
    if (
      config.mode === 'batch' ||
      !fields.length ||
      config.outputs?.version !== 1 ||
      outputFieldsError(fields) ||
      !config.responseMap ||
      Object.keys(config.responseMap).length !== fields.length ||
      fields.some(
        (field) =>
          !webhookResponsePathValid(config.responseMap?.[field.key] ?? '')
      )
    )
      return 'Map each per-record output to a path like $.data.score';
  }
  if (!bounded(config.maxCallsPerPeriod ?? 1000, 10000))
    return 'Up to 10,000 calls a period';
}

export function changeWebhookMode(
  config: WebhookConfig,
  mode: NonNullable<WebhookConfig['mode']>
): WebhookConfig {
  return mode === 'batch'
    ? { ...config, mode, outputs: null, responseMap: null }
    : { ...config, mode };
}

export function changeWebhookOutputs(
  config: WebhookConfig,
  fields: RecordSkillOutput[]
): WebhookConfig {
  return {
    ...config,
    outputs: outputSchema(fields),
    responseMap: fields.length
      ? Object.fromEntries(
          fields.map((field) => [
            field.key,
            config.responseMap?.[field.key] ?? '',
          ])
        )
      : null,
  };
}

export function webhookHost(url: string): string {
  try {
    return new URL(url).hostname;
  } catch {
    return '';
  }
}

export function defaultWebhookBody(mode: WebhookConfig['mode']): string {
  const record = {
    id: 'sample',
    kind: 'person',
    first_name: 'Ada',
    last_name: 'Ng',
    email: 'ada@example.com',
    job_title: 'VP Sales',
    linkedin_url: null,
    company_name: 'Example',
    company_domain: 'example.com',
    crm: { source: null, object: null, record_id: null },
  };
  return JSON.stringify(
    {
      event: mode === 'batch' ? 'play.records' : 'play.record',
      test: false,
      play: { id: 'play-id', name: 'Territory routing' },
      run: { id: 'run-id', link: 'https://app.cleanlist.ai/agents/…' },
      ...(mode === 'batch' ? { records: [record] } : { record }),
    },
    null,
    2
  );
}
